import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MEGA_MENU } from "@/data/megaMenu";

/* Returns the static MEGA_MENU structure with every user-facing string
 * (label, heading, tagline, spotlight title/body/cta etc.) routed
 * through `t()`. Keys are derived from the English source so the
 * fallback always renders sensible copy if a locale is missing a key.
 *
 * Translation keys live under the `menu.*` namespace. Pattern:
 *   menu.cat_<entry.key>            → top-level button label
 *   menu.heading.<slug>             → column heading
 *   menu.label.<slug>               → link label (slug = lowercased identifier)
 *   menu.tagline.<slug>             → tagline under a link
 *   menu.program.<id>               → program name (Instant Funding etc.)
 *   menu.spot.<entry.key>.title|body|eyebrow|cta
 *
 * `slug` is just a stable kebab-case form of the English text. */

const slug = (s = "") =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export function useTranslatedMegaMenu() {
  const { t, i18n } = useTranslation();
  // Re-memoise when the language changes so React re-renders downstream.
  return useMemo(() => {
    return MEGA_MENU.map((entry) => ({
      ...entry,
      label: t(`menu.cat_${entry.key}`, entry.label),
      programs: entry.programs?.map((p) => ({
        ...p,
        label: t(`menu.program.${p.id}`, p.label),
      })),
      columns: entry.columns?.map((col) => ({
        ...col,
        heading: t(`menu.heading.${slug(col.heading)}`, col.heading),
        items: col.items.map((it) => ({
          ...it,
          label: t(`menu.label.${slug(it.label)}`, it.label),
          tagline: it.tagline ? t(`menu.tagline.${slug(it.label)}`, it.tagline) : it.tagline,
        })),
      })),
      spotlight: entry.spotlight && {
        ...entry.spotlight,
        eyebrow: t(`menu.spot.${entry.key}.eyebrow`, entry.spotlight.eyebrow),
        title: t(`menu.spot.${entry.key}.title`, entry.spotlight.title),
        body: t(`menu.spot.${entry.key}.body`, entry.spotlight.body),
        cta: entry.spotlight.cta && {
          ...entry.spotlight.cta,
          label: t(`menu.spot.${entry.key}.cta`, entry.spotlight.cta.label),
        },
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);
}
